<template>
  <header
    id="header"
    ref="header"
    class="border-static-default-low sticky top-0 z-10 border-b-1 transition-all duration-200"
    :class="{ '-translate-y-full': headerIsHidden && !isOpen }"
  >
    <div id="burger-header" class="block md:hidden">
      <div
        class="bg-static-default-low flex flex-wrap items-center justify-between gap-x-16 gap-y-4 py-8"
      >
        <MenuIcon class="flex-1 pl-20" />
        <TheLogo class="h-12" />
        <SearchBar
          class="z-[1] order-last w-full grow"
          :class="{ 'px-20': !isBuybackEnabled }"
          :header-is-hidden
          :is-buyback-enabled
        />
        <UserOptions class="flex-1 pr-20" />
        <NavBurgerMenu v-if="isOpen" />
      </div>
    </div>
    <div id="fullscreen-header" class="bg-static-default-low hidden md:block">
      <RevContainer
        :class="{ 'bg-surface-default-hi': !hasImproveSearchUIExperiment }"
      >
        <TopHeader class="relative z-[2]" />
      </RevContainer>

      <RevContainer :class="hasImproveSearchUIExperiment ? 'py-4' : 'py-8'">
        <div class="flex items-center gap-16 lg:gap-32">
          <TheLogo class="h-24" />
          <SearchBar class="z-[1] grow" :header-is-hidden />
          <div class="flex items-center gap-4 lg:gap-8">
            <TradeInButton v-if="isBuybackEnabled" class="-mb-1" />
            <RevLink
              v-if="!helpLinkOnTop"
              class="text-nowrap px-12 py-8"
              :to="`/${locale}/help`"
              :tracking="{ name: i18n(translations.help), zone: 'header' }"
              :underlined="false"
              >{{ i18n(translations.help) }}</RevLink
            >
            <UserIcon />
            <CartIcon data-test="icon-cart" />
          </div>
        </div>
      </RevContainer>

      <NavMenu class="h-48" />
    </div>
    <ClientOnly>
      <CountrySwitchRedirection
        v-if="hasCountrySwitchRedirection"
        ref="countrySwitchBannerRef"
      />
    </ClientOnly>
  </header>
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevContainer } from '@ds/components/Container'
import { RevLink } from '@ds/components/Link'

import SearchBar from '~/scopes/search-bar/SearchBar.vue'
import { useToggleSearchBar } from '~/scopes/search-bar/composables/useToggleSearchBar'

import { useHideOnScroll } from '../composables/useHideOnScroll'
import { useNavigation } from '../composables/useNavigation'

import CartIcon from './CartIcon.vue'
import CountrySwitchRedirection from './CountrySwitch/CountrySwitchRedirection.vue'
import MenuIcon from './MenuIcon.vue'
import NavBurgerMenu from './NavBurgerMenu/NavBurgerMenu.vue'
import NavMenu from './NavMenu/NavMenu.vue'
import translations from './TheHeader.translations'
import TheLogo from './TheLogo.vue'
import TopHeader from './TopHeader.vue'
import TradeInButton from './TradeInButton.vue'
import UserIcon from './UserIcon.vue'
import UserOptions from './UserOptions.vue'

const { isOpen } = useNavigation()
const i18n = useI18n()
const locale = useI18nLocale()

const { hidden: headerIsHidden } = useHideOnScroll()
const { clearActiveMenu } = useNavigation()
const experiments = useExperiments()
const { closeSearchBar } = useToggleSearchBar()

watchEffect(() => {
  if (headerIsHidden.value) {
    clearActiveMenu()
    closeSearchBar()
  }
})

const {
  features,
}: {
  features: {
    buyback: { enabled: boolean }
  }
} = useMarketplace()

const isBuybackEnabled = computed(() => features.buyback.enabled)

const hasCountrySwitchRedirection =
  experiments['experiment.countrySwitchRedirection'] === 'withRedirection'

const hasImproveSearchUIExperiment =
  experiments['experiment.improveSearchUi'] === 'new'

const helpLinkOnTop =
  experiments['experiment.helpHeader'] === 'top' || hasImproveSearchUIExperiment
</script>
